import { useState, useEffect, useCallback, useContext, Suspense, useRef } from "react";
import { Button, Backdrop, makeStyles } from "@material-ui/core";
import SpeedDial from "@material-ui/lab/SpeedDial";
import SpeedDialIcon from "@material-ui/lab/SpeedDialIcon";
import SpeedDialAction from "@material-ui/lab/SpeedDialAction";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../../../../constants/caption/dashboardHeader";
import { LincolnFormActual, LincolnFormPrint } from "../../../../../../";
import { useReactToPrint } from "react-to-print";

import GetAppIcon from "@material-ui/icons/GetApp";
import EditIcon from "@material-ui/icons/Edit";
import PublishIcon from "@material-ui/icons/Publish";
import SendIcon from "@material-ui/icons/Send";

import "./lincolnForm.css";
import { withStyles, Theme } from "@material-ui/core/styles";
import { GLTD_Class_2_Employer_Section, GLTD_Class_2_payload } from "../../../../../../../../@types/lincolnForm.types";
import { AdminEnrollmentInitDataQuery } from "../../../../../../../../utils/api/pages/enrollmentInit";
import useRouteParam from "../../../../../../../../hooks/useRouteParam";
import { api } from "../../../../../../../../utils/api";
import { Member } from "../../../../../../../../@types/member.types";
import { ThemeContext } from "../../../../../../../../contexts";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { trackPromise } from "react-promise-tracker";
import ExtractRouteParams from "../../../../../../../../utils/commonFunctions/extractRouteParams";
import { useNavigate } from "react-router-dom";
import ExtractRouteQueryParams from "../../../../../../../../utils/commonFunctions/extractRouteQueryParams";
import prefixCharacterAddition from "../../../../../../../../utils/commonFunctions/prefixCharacterAddition";

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		height: 380,
		transform: "translateZ(0px)",
		flexGrow: 1
	},
	alert: {
		width: "100%",
		"& > * + *": {
			marginTop: theme.spacing(2)
		}
	},
	speedDial: {
		position: "fixed",
		bottom: theme.spacing(2),
		right: theme.spacing(2)
	}
}));

const LincolnForm = (): JSX.Element => {
	const classes = useStyles();
	const { setTheme } = useContext(ThemeContext);
	const [employerSection, setEmployerSection] = useState<GLTD_Class_2_Employer_Section>({
		billing_division: "",
		sort_code: "",
		payroll_cycle: "",
		policy: "",
		average_hours: null,
		employment_status: "",
		occupation: "",
		earnings: null,
		earning_status: "",
		date_of_employment: {
			day: null,
			month: null,
			year: null
		},
		active_work_status: null,
		date_of_rehire: {
			day: null,
			month: null,
			year: null
		}
	});
	const [openSpeedDial, setOpenSpeedDial] = useState(false);
	const [valueChange, setValueChange] = useState(false);
	const componentRef = useRef(null);
	const handleDownloadClick = useReactToPrint({
		content: () => componentRef.current,
		documentTitle: "Lincoln Form"
	});
	const queryPlanCode = useRouteParam("pc");
	const queryGroupNumber = useRouteParam("gn");
	const querySSN = useRouteParam("qs");
	const queryEnrollmentId = useRouteParam("eid");
	const routeQueryParam = ExtractRouteQueryParams().join("&");
	const navigate = useNavigate();
	const [employee, setEmployee] = useState<Member>();
	const [totalPremium, setTotalPremium] = useState<number>();
	const [enrollmentStatus, setEnrollmentStatus] = useState<string>("");
	const [enrollmentDate, setEnrollmentDate] = useState<string>("");
	const [effectiveDate, setEffectiveDate] = useState("");
	const [planName, setPlanName] = useState("");

	const handleOpenSpeedDial = () => setOpenSpeedDial(true);

	const handleCloseSpeedDial = () => setOpenSpeedDial(false);

	const handleEnrollmentChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name } = event.target;
			const { value } = event.target;

			if (name === "average_hours") {
				if (!isNaN(value as any)) {
					setEmployerSection(Object.assign({}, employerSection, { [name]: value }));
				} else {
					event.target.value = "";
					event.target.value = employerSection.average_hours as unknown as string;
				}
			} else if (name === "earnings") {
				if (!isNaN(value as any)) {
					setEmployerSection(Object.assign({}, employerSection, { [name]: value }));
				} else {
					event.target.value = "";
					event.target.value = employerSection.earnings as unknown as string;
				}
			} else {
				setEmployerSection(Object.assign({}, employerSection, { [name]: value }));
			}

			setValueChange(true);
		},
		[employerSection]
	);

	const handleApproveEffectiveDateChange = useCallback(
		(_date: MaterialUiPickersDate, name: string | null | undefined) => {
			const date = new Date(_date as Date);
			const parsedDate = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
			setEffectiveDate(parsedDate);
		},
		[]
	);

	// Handle Enrollment of Date Change
	const handleEnrollmentDateChange = useCallback(
		(event: any) => {
			const { name, value } = event.target;
			if (name === "day") {
				if (!isNaN(value)) {
					if (value.length <= 2 && value <= 31) {
						setEmployerSection(
							Object.assign(
								{},
								{
									...employerSection,
									date_of_employment: {
										...employerSection.date_of_employment,
										day: value
									}
								}
							)
						);
					} else {
						event.target.value = employerSection.date_of_employment.day;
						alert("Please enter a valid date!");
					}
				} else {
					event.target.value = null;
					event.target.value = employerSection.date_of_employment.day;
				}
			} else if (name === "month") {
				if (!isNaN(value)) {
					if (value.length <= 2 && value <= 12) {
						setEmployerSection(
							Object.assign(
								{},
								{
									...employerSection,
									date_of_employment: {
										...employerSection.date_of_employment,
										month: value
									}
								}
							)
						);
					} else {
						event.target.value = employerSection.date_of_employment.month;
						alert("Please enter a valid Month!");
					}
				} else {
					event.target.value = null;
					event.target.value = employerSection.date_of_employment.month;
				}
			} else if (name === "year") {
				if (!isNaN(value)) {
					if (value.length <= 4) {
						setEmployerSection(
							Object.assign(
								{},
								{
									...employerSection,
									date_of_employment: {
										...employerSection.date_of_employment,
										year: value
									}
								}
							)
						);
					} else {
						event.target.value = employerSection.date_of_employment.year;
						alert("Please enter a valid year!");
					}
				} else {
					event.target.value = null;
					event.target.value = employerSection.date_of_employment.year;
				}
			}

			setValueChange(true);
		},
		[employerSection]
	);

	// Handle Rehire of Date Change
	const handleRehireDateChange = useCallback(
		(event: any) => {
			const { name, value } = event.target;
			if (name === "day") {
				if (!isNaN(value)) {
					if (value.length <= 2 && value <= 31) {
						setEmployerSection(
							Object.assign(
								{},
								{
									...employerSection,
									date_of_rehire: {
										...employerSection.date_of_rehire,
										day: value
									}
								}
							)
						);
					} else {
						event.target.value = employerSection.date_of_rehire.day;
						alert("Please enter a valid date!");
					}
				} else {
					event.target.value = null;
					event.target.value = employerSection.date_of_rehire.day;
				}
			} else if (name === "month") {
				if (!isNaN(value)) {
					if (value.length <= 2 && value <= 12) {
						setEmployerSection(
							Object.assign(
								{},
								{
									...employerSection,
									date_of_rehire: {
										...employerSection.date_of_rehire,
										month: value
									}
								}
							)
						);
					} else {
						event.target.value = employerSection.date_of_rehire.month;
						alert("Please enter a valid Month!");
					}
				} else {
					event.target.value = null;
					event.target.value = employerSection.date_of_rehire.month;
				}
			} else if (name === "year") {
				if (!isNaN(value)) {
					if (value.length <= 4) {
						setEmployerSection(
							Object.assign(
								{},
								{
									...employerSection,
									date_of_rehire: {
										...employerSection.date_of_rehire,
										year: value
									}
								}
							)
						);
					} else {
						event.target.value = employerSection.date_of_rehire.year;
						alert("Please enter a valid year!");
					}
				} else {
					event.target.value = null;
					event.target.value = employerSection.date_of_rehire.year;
				}
			}

			setValueChange(true);
		},
		[employerSection]
	);

	const handleCheck = useCallback(
		({ target: { checked, name } }: React.ChangeEvent<HTMLInputElement>) => {
			let field: any, value;

			if (name === "full_time" && checked === true) {
				field = "employment_status";
				value = "full_time";
			} else if (name === "part_time" && checked === true) {
				field = "employment_status";
				value = "part_time";
			} else if (name === "yes" && checked === true) {
				field = "active_work_status";
				value = true;
			} else if (name === "no" && checked === true) {
				field = "active_work_status";
				value = false;
			} else if (name === "hourly" && checked === true) {
				field = "earning_status";
				value = "hourly";
			} else if (name === "weekly" && checked === true) {
				field = "earning_status";
				value = "weekly";
			} else if (name === "monthly" && checked === true) {
				field = "earning_status";
				value = "monthly";
			} else if (name === "yearly" && checked === true) {
				field = "earning_status";
				value = "yearly";
			} else if (name === "male" && checked === true) {
				field = "gender";
				value = "male";
			} else if (name === "female" && checked === true) {
				field = "gender";
				value = "female";
			} else if (name === "married" && checked === true) {
				field = "marital_status";
				value = "married";
			} else if (name === "single" && checked === true) {
				field = "marital_status";
				value = "single";
			} else if (name === "enroll_insurance" && checked === true) {
				field = "enroll_insurance";
				value = true;
			} else if (name === "enroll_insurance" && checked === false) {
				field = "enroll_insurance";
				value = false;
			} else if (name === "buy_up_yes" && checked === true) {
				field = "buy_up_ltd";
				value = true;
			} else if (name === "buy_up_no" && checked === true) {
				field = "buy_up_ltd";
				value = false;
			}

			// console.log("clicked");
			setEmployerSection(Object.assign({}, employerSection, { [field]: value }));
			setValueChange(true);
		},
		[employerSection]
	);

	const handleRedirectToMemberDetails = useCallback(() => {
		const pathname = `/employer/member?${routeQueryParam}&&to=benefits`;
		navigate(pathname);
	}, [navigate, routeQueryParam]);

	const handleSubmitClick = useCallback(async () => {
		try {
			const { date_of_employment, date_of_rehire } = employerSection;
			const payload: GLTD_Class_2_payload = {
				_id: queryEnrollmentId as string,
				plan_name: planName,
				effective_date: effectiveDate,
				gltd_class_2_employer_section: {
					...employerSection,
					date_of_employment:
						date_of_employment.year + "-" + date_of_employment.month + "-" + date_of_employment.day,
					date_of_rehire: date_of_rehire.year + "-" + date_of_rehire.month + "-" + date_of_rehire.day
				}
			};

			if (effectiveDate === "") {
				alert("Effective date cannot be empty");
			}

			const response: any = await trackPromise(api.enrollments.AddAutoApproveEnrollment(payload));

			// console.log("Lincoln Response", response);
			if (response) {
				alert("Approve Enrollment Added Successfully!");
				handleRedirectToMemberDetails();
			}

			setValueChange(false);
		} catch (error) {
			alert("Auto Approve Enrollment Submission unsuccessful!");
			throw error;
		}
	}, [effectiveDate, employerSection, handleRedirectToMemberDetails, planName, queryEnrollmentId]);

	const getAdminEnrollmentInitData = useCallback(async () => {
		if (!queryGroupNumber || !queryPlanCode || !querySSN || !queryEnrollmentId) {
			return;
		}
		const query: AdminEnrollmentInitDataQuery = {
			insured_SSN: querySSN as string,
			plan_code: queryPlanCode as string,
			group_number: queryGroupNumber as number,
			enrollment_status: "ACTIVE"
		};

		const { enrollments, theme } = await trackPromise(api.pages.getAdminEnrollmentInitData(query));
		setTheme(Object.assign({}, theme));
		const {
			employee,
			total_premium,
			enrollment_date,
			enrollment_status,
			plan_name,
			effective_date,
			gltd_class_2_lincoln_employer_section
		} = enrollments[0];

		const _employerSection = {
			...gltd_class_2_lincoln_employer_section,
			date_of_employment: {
				day: prefixCharacterAddition(
					new Date(gltd_class_2_lincoln_employer_section.date_of_employment as string).getDate()
				),
				month: prefixCharacterAddition(
					new Date(gltd_class_2_lincoln_employer_section.date_of_employment as string).getMonth() + 1
				),
				year: new Date(gltd_class_2_lincoln_employer_section.date_of_employment as string).getFullYear()
			},
			date_of_rehire: {
				day: prefixCharacterAddition(
					new Date(gltd_class_2_lincoln_employer_section.date_of_rehire as string).getDate()
				),
				month: prefixCharacterAddition(
					new Date(gltd_class_2_lincoln_employer_section.date_of_rehire as string).getMonth() + 1
				),
				year: new Date(gltd_class_2_lincoln_employer_section.date_of_rehire as string).getFullYear()
			}
		};

		setEmployerSection(Object.assign({}, _employerSection as unknown as GLTD_Class_2_Employer_Section));
		setEmployee(Object.assign({}, employee));
		setTotalPremium(total_premium as number);
		setEnrollmentDate(enrollment_date as unknown as string);
		setEnrollmentStatus(enrollment_status);
		setPlanName(plan_name);
		setEffectiveDate(effective_date as unknown as string);
	}, [queryEnrollmentId, queryGroupNumber, queryPlanCode, querySSN, setTheme]);

	const speedDialActions = [
		// {
		// 	icon: <PublishIcon />,
		// 	name: "Submit",
		// 	callback: handleSubmitClick
		// },
		{
			icon: <GetAppIcon />,
			name: "Download",
			callback: handleDownloadClick
		}
	];

	// console.log("Component Ref", componentRef);

	useEffect(() => {
		getAdminEnrollmentInitData();
	}, [getAdminEnrollmentInitData]);

	if (queryEnrollmentId === null) return <></>;

	return (
		<div className="lincoln-form-root-container">
			<Backdrop open={openSpeedDial} style={{ zIndex: 1009 }} />
			<SpeedDial
				ariaLabel="SpeedDial tooltip example"
				className={valueChange ? `${classes.speedDial} bounce2` : ` ${classes.speedDial}`}
				icon={<SpeedDialIcon />}
				// sx={{ position: "fixed", bottom: 16, right: 16 }}
				onClose={handleCloseSpeedDial}
				onOpen={handleOpenSpeedDial}
				open={openSpeedDial}
				FabProps={{
					style: {
						//backgroundColor: theme?.button?.color
					}
				}}
			>
				{speedDialActions.map((action, index) => {
					const { name, icon, callback } = action;
					return (
						<SpeedDialAction key={index} icon={icon} tooltipTitle={name} tooltipOpen onClick={callback} />
					);
				})}
			</SpeedDial>
			<>
				{employee !== undefined ? (
					<>
						<LincolnFormActual
							employerSection={employerSection}
							employee={employee as Member}
							totalPremium={totalPremium as number}
							planCode={queryPlanCode as string}
							enrollmentDate={enrollmentDate}
							enrollmentStatus={enrollmentStatus}
							effectiveDate={effectiveDate}
							handleApproveEffectiveDateChange={handleApproveEffectiveDateChange}
							handleEnrollmentChange={handleEnrollmentChange}
							handleEnrollmentDateChange={handleEnrollmentDateChange}
							handleRehireDateChange={handleRehireDateChange}
							handleCheck={handleCheck}
						/>
						<div style={{ display: "none" }}>
							<Suspense fallback={<div />}>
								<LincolnFormPrint
									employerSection={employerSection}
									employee={employee as Member}
									effectiveDate={effectiveDate}
									enrollmentDate={enrollmentDate}
									totalPremium={totalPremium as number}
									ref={componentRef}
								/>
							</Suspense>
						</div>
						<div className="enrollment-action-button-container" id="enrollment-action-button-container">
							<Button
								className="button-green"
								onClick={handleSubmitClick}
								variant="contained"
								style={{
									pointerEvents: "unset"
								}}
							>
								<span className="button-label-with-icon">Submit</span>
								<span>
									<SendIcon className="button-icon" />
								</span>
							</Button>
						</div>
					</>
				) : null}
			</>
		</div>
	);
};

export default LincolnForm;
